import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { FormCheck, Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { DialogAction } from '@smart-builder/types';
import { _t, SBStorage } from '@smart-builder/util';
import { Icon } from '../base/Icon';
import { SBButton } from '../base/SBButton';
import { currentDialogSelector, useCloseDialog } from './ducks';
import {
  dialogActionCaptions,
  DialogBody,
  DialogElem,
  DialogType,
  dialogTypeClassNames,
  dialogTypeIcons,
} from './ducks/types';
import './SBDialog.scss';

type SBDialogBodyProps = PropsWithChildren<DialogBody>;
const SBDialogBody: React.FC<SBDialogBodyProps> = ({
  title,
  children,
  ...props
}) => {
  const message = props.message.split('\n');
  const dialogType = props.dialogType ?? DialogType.None;
  const icon = props.icon ?? dialogTypeIcons[dialogType];
  const className = `sb-dialog-inner dialog-body-${dialogTypeClassNames[dialogType]}`;

  return (
    <Modal.Body className={className}>
      {(icon || title) && (
        <div className="dialog-header">
          {icon && <Icon icon={icon} />}
          {title && <div className="dialog-title">{title}</div>}
        </div>
      )}
      <div className="dialog-body">
        {message.length > 1
          ? message.map((paragraph, index) => <p key={index}>{paragraph}</p>)
          : message}
        {children}
      </div>
    </Modal.Body>
  );
};

const defaultOnGetOptionalBypass = (key: string): boolean => {
  return SBStorage.getItem('key') === 'true';
};

const defaultOnSetOptionalBypass = (key: string, bypass: boolean) => {
  return SBStorage.setItem('key', bypass ? 'true' : 'false');
};

type SBDialogModalOptionalProps = {
  onGetOptionalBypass?: (key: string) => boolean;
  onSetOptionalBypass?: (key: string, bypass: boolean) => void;
};
type SBDialogModalProps = DialogElem & SBDialogModalOptionalProps;
const SBDialogModal: React.FC<SBDialogModalProps> = ({
  body,
  bodyOverride,
  buttons,
  className,
  closeOnBackground,
  optional,
  onGetOptionalBypass,
  onSetOptionalBypass,
}) => {
  const autoFocusedButtonRef = useRef<HTMLButtonElement>(null);

  const closeDialog = useCloseDialog();

  const _onGetOptionalBypass =
    onGetOptionalBypass ?? defaultOnGetOptionalBypass;
  const _onSetOptionalBypass =
    onSetOptionalBypass ?? defaultOnSetOptionalBypass;

  const [bypass, setBypass] = useState(false);
  useEffect(() => {
    if (optional && _onGetOptionalBypass(optional.key)) {
      closeDialog(optional.result);
    }
    // Only run on mounting
    // eslint-disable-next-line
  }, []);

  const optionalResult = optional?.result;
  const onActionClick = useCallback(
    (action: DialogAction) => {
      if (optional && action === optionalResult && bypass) {
        _onSetOptionalBypass(optional.key, true);
      }
      closeDialog(action);
    },
    [bypass, closeDialog, optionalResult, optional, _onSetOptionalBypass],
  );

  const onCheckChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setBypass(event.target.checked);
    },
    [],
  );

  return (
    <Modal
      show
      centered
      role="dialog"
      dialogClassName="dialog-modal"
      className={classNames(['dialog', className])}
      backdrop={closeOnBackground ? undefined : 'static'}
      keyboard={closeOnBackground}
      onShow={() => autoFocusedButtonRef?.current?.focus()}
      onHide={closeDialog}
    >
      {body && (
        <SBDialogBody {...body}>
          {optional && (
            // SS 20230911 Using a Bootstrap checkbox here rather than an UnboundControl as SBDialog
            // is also used by login-rest which we want to avoid bloating with DevEx controls
            <FormCheck
              onChange={onCheckChange}
              checked={bypass}
              label={_t("Don't show again")}
            />
          )}
        </SBDialogBody>
      )}
      {bodyOverride}

      {buttons.length > 0 && (
        <Modal.Footer
          className={classNames([
            'dialog-footer',
            `action-count-${buttons.length}`,
          ])}
        >
          {buttons.map(({ action, caption, variant, autoFocus }) => (
            <SBButton
              key={action}
              id={`sb-dialog-btn-${dialogActionCaptions[action]}`} // don't translate, this is used by tests for finding buttons
              innerRef={autoFocus ? autoFocusedButtonRef : undefined}
              className="dialog-button"
              onClick={() => onActionClick(action)}
              variant={variant}
            >
              {caption}
            </SBButton>
          ))}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export const SBDialogContainer: React.FC<SBDialogModalOptionalProps> = ({
  onGetOptionalBypass,
  onSetOptionalBypass,
}) => {
  const dialog = useSelector(currentDialogSelector);
  return (
    <div id="dialog-container">
      {dialog && (
        <SBDialogModal
          {...dialog}
          onGetOptionalBypass={onGetOptionalBypass}
          onSetOptionalBypass={onSetOptionalBypass}
        />
      )}
    </div>
  );
};
